/**
 * CountUp + StaggerCard — Phase 13 Processing Fluency.
 *
 * CountUp tween-animates a number from `from` to `value` using
 * requestAnimationFrame with ease-out cubic (slows near the target —
 * reinforces Goal Gradient feel). Respects `prefers-reduced-motion`.
 *
 * StaggerCard wraps children with a `translateY(20px) + opacity 0 → 1`
 * entry animation, delayed by `index * step` ms.
 */
import React, { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '../../design-system/hooks/usePrefersReducedMotion';

// ─── Styles (shared, injected once) ─────────────────────────────────────
const STYLE_ID = 'tajer-dashboard-anims';
const STYLE_CSS = `
@keyframes tjr-card-in {
  0%   { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes tjr-alive-pulse {
  0%, 100% { transform: scale(1);   opacity: 1; }
  50%      { transform: scale(1.25); opacity: 0.7; }
}
@media (prefers-reduced-motion: reduce) {
  .tjr-card-in, .tjr-alive-pulse {
    animation: none !important;
  }
}
`;
function ensureAnimStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = STYLE_CSS;
  document.head.appendChild(el);
}

// ease-out cubic — slows near target; reinforces Goal Gradient feel.
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

export default function CountUp({
  value = 0,
  from = 0,
  duration = 800,
  format = (n) => n.toLocaleString('ar-SA', { maximumFractionDigits: 0 }),
  suffix = '',
  className = '',
}) {
  const reduced = usePrefersReducedMotion();
  const [display, setDisplay] = useState(reduced ? value : from);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(from);
  const targetRef = useRef(value);

  useEffect(() => {
    if (reduced) {
      setDisplay(value);
      return undefined;
    }

    fromRef.current = display;
    targetRef.current = Number(value) || 0;
    startRef.current = null;

    const step = (ts) => {
      if (startRef.current == null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      const next = fromRef.current + (targetRef.current - fromRef.current) * eased;
      setDisplay(next);
      if (t < 1) {
        rafRef.current = window.requestAnimationFrame(step);
      }
    };

    rafRef.current = window.requestAnimationFrame(step);
    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, reduced]);

  return (
    <span className={`tabular-nums font-mono ${className}`}>
      {format(display)}
      {suffix}
    </span>
  );
}

// ─── StaggerCard ────────────────────────────────────────────────────────
/**
 * Wraps a block so it fades + slides in with an index-based delay.
 * Reduced-motion renders with no animation.
 */
export function StaggerCard({
  index = 0,
  step = 100,
  duration = 300,
  className = '',
  children,
  as: Tag = 'div',
  ...rest
}) {
  const reduced = usePrefersReducedMotion();
  useEffect(() => {
    ensureAnimStyles();
  }, []);
  const style = reduced
    ? undefined
    : {
        animation: `tjr-card-in ${duration}ms ease-out both`,
        animationDelay: `${index * step}ms`,
      };
  return (
    <Tag className={`tjr-card-in ${className}`} style={style} {...rest}>
      {children}
    </Tag>
  );
}

// ─── AlivePulse ─────────────────────────────────────────────────────────
/**
 * Small, subtle pulse dot — for "system is alive" indicators.
 */
export function AlivePulse({ className = '', color = 'bg-success-500' }) {
  const reduced = usePrefersReducedMotion();
  useEffect(() => {
    ensureAnimStyles();
  }, []);
  return (
    <span
      className={`tjr-alive-pulse inline-block h-2 w-2 rounded-full ${color} ${className}`}
      style={reduced ? undefined : { animation: 'tjr-alive-pulse 2s ease-in-out infinite' }}
      aria-hidden="true"
    />
  );
}

// ─── KPISkeleton ────────────────────────────────────────────────────────
/**
 * Placeholder shaped like the KPI card in UnifiedDashboard.
 */
export function KPISkeleton({ count = 4, className = '' }) {
  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="bg-white/10 backdrop-blur-sm rounded-xl p-3 ring-1 ring-white/15 animate-pulse"
          aria-hidden="true"
        >
          <div className="h-3 w-2/3 bg-white/20 rounded mb-2" />
          <div className="h-5 w-1/2 bg-white/30 rounded" />
        </div>
      ))}
    </div>
  );
}

// ─── DashboardSkeleton ──────────────────────────────────────────────────
/**
 * Full-page skeleton that mirrors UnifiedDashboard overview shape so the
 * layout does not jump when real data arrives.
 */
export function DashboardSkeleton() {
  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-indigo-50/30 pb-10"
      dir="rtl"
      role="status"
      aria-label="جاري تحميل لوحة القيادة"
      aria-busy="true"
    >
      {/* Hero banner skeleton */}
      <div className="bg-gradient-to-l from-blue-700 via-indigo-700 to-violet-800 rounded-2xl mx-4 mt-4 p-6 shadow-xl">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-white/10 animate-pulse" />
            <div className="space-y-2">
              <div className="h-5 w-56 rounded bg-white/20 animate-pulse" />
              <div className="h-3 w-72 rounded bg-white/10 animate-pulse" />
            </div>
          </div>
          <div className="h-20 w-20 rounded-full bg-white/10 animate-pulse" />
        </div>
        <div className="mt-5">
          <KPISkeleton count={4} />
        </div>
      </div>

      {/* Tab bar skeleton */}
      <div className="mx-4 mt-4">
        <div className="h-12 w-full rounded-2xl bg-neutral-200 animate-pulse" />
      </div>

      {/* Body skeleton */}
      <div className="mx-4 mt-4 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm animate-pulse"
              aria-hidden="true"
            >
              <div className="h-8 w-8 rounded-xl bg-neutral-200 mb-3" />
              <div className="h-5 w-20 rounded bg-neutral-200 mb-1" />
              <div className="h-3 w-28 rounded bg-neutral-100" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm space-y-3 animate-pulse"
              aria-hidden="true"
            >
              <div className="h-4 w-32 rounded bg-neutral-200" />
              <div className="h-24 w-full rounded-xl bg-neutral-100" />
              <div className="h-3 w-5/6 rounded bg-neutral-100" />
              <div className="h-3 w-2/3 rounded bg-neutral-100" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
